<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SamlSettings from './components/SamlSettings.vue';
import SamlPaywall from './components/SamlPaywall.vue';
import samlSettingsAPI from 'dashboard/api/samlSettings';

import { usePolicy } from 'dashboard/composables/usePolicy';
import { INSTALLATION_TYPES } from 'dashboard/constants/installationTypes';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
const { shouldShow, shouldShowPaywall } = usePolicy();
const { t } = useI18n();

const allowedLoginMethods = computed(
  () => window.vhConfig.allowedLoginMethods || ['email']
);

const isSamlSsoEnabled = computed(() =>
  allowedLoginMethods.value.includes('saml')
);

const shouldShowSaml = computed(() => {
  const hasPermission = shouldShow(
    FEATURE_FLAGS.SAML,
    ['administrator'],
    [INSTALLATION_TYPES.CLOUD, INSTALLATION_TYPES.ENTERPRISE]
  );
  return hasPermission && isSamlSsoEnabled.value;
});

const showPaywall = computed(() => shouldShowPaywall('saml'));

const isAutoManaged = ref(false);
const samlStatus = ref({ ssoUrl: '', spEntityId: '', idpEntityId: '' });
const isLoading = ref(true);

const loadSamlStatus = async () => {
  try {
    const response = await samlSettingsAPI.get();
    const settings = response.data;
    if (settings.sso_url) {
      samlStatus.value = {
        ssoUrl: settings.sso_url,
        spEntityId: settings.sp_entity_id || '',
        idpEntityId: settings.idp_entity_id || '',
      };
      isAutoManaged.value = true;
    }
  } catch {
    isAutoManaged.value = false;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if (shouldShowSaml.value) {
    loadSamlStatus();
  } else {
    isLoading.value = false;
  }
});
</script>

<template>
  <SettingsLayout
    class="max-w-2xl mx-auto"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('SECURITY_SETTINGS.TITLE')"
        :description="$t('SECURITY_SETTINGS.DESCRIPTION')"
        :link-text="$t('SECURITY_SETTINGS.LINK_TEXT')"
        feature-name="saml"
      />
    </template>
    <template #body>
      <SamlPaywall v-if="showPaywall" />
      <template v-else-if="shouldShowSaml">
        <template v-if="isAutoManaged && !isLoading">
          <div class="mt-4 rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <div class="flex items-center gap-2 mb-4">
              <span
                class="inline-block w-2.5 h-2.5 rounded-full bg-green-500"
              />
              <h3 class="text-base font-medium text-n-slate-12">
                SAML SSO {{ t('SECURITY_SETTINGS.SAML.TITLE') }}
              </h3>
            </div>
            <div class="space-y-3 text-sm text-n-slate-11">
              <div class="flex gap-2">
                <span class="font-medium text-n-slate-12 min-w-[120px]">
                  SSO URL:
                </span>
                <span>{{ samlStatus.ssoUrl }}</span>
              </div>
              <div class="flex gap-2">
                <span class="font-medium text-n-slate-12 min-w-[120px]">
                  IDP Entity ID:
                </span>
                <span>{{ samlStatus.idpEntityId }}</span>
              </div>
              <div class="flex gap-2">
                <span class="font-medium text-n-slate-12 min-w-[120px]">
                  SP Entity ID:
                </span>
                <span>{{ samlStatus.spEntityId }}</span>
              </div>
            </div>
          </div>
        </template>
        <SamlSettings v-else-if="!isLoading" />
      </template>
      <div v-else class="mt-6 text-sm text-slate-600">
        {{ $t('SECURITY_SETTINGS.SAML_DISABLED_MESSAGE') }}
      </div>
    </template>
  </SettingsLayout>
</template>
